// src/pages/marketplace/MarketplacePage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Carousel from '../../components/common/Carousel';
import { browseShops } from '../../services/marketplaceService';
import '../../styles/MarketplacePage.css';

export default function MarketplacePage() {
  const navigate = useNavigate();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const fetchShops = async () => {
      try {
        const params = { limit: 50 };
        if (debouncedSearch) params.q = debouncedSearch;
        const res = await browseShops(params);
        if (!cancelled) {
          setShops(res?.data?.data.items || []);
        }
      } catch {
        if (!cancelled) setError('Failed to load artist shops.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchShops();
    return () => { cancelled = true; };
  }, [debouncedSearch]);

  const handleShopClick = (shop) => {
    if (shop?.artist_id) {
      navigate(`/marketplace/shop/${shop.artist_id}`);
    }
  };

  return (
    <div className="mp-page">
      <div className="mp-header">
        <h1 className="mp-title">
          <span className="mp-title-static">Discover</span>{' '}
          <span className="mp-title-accent" style={{ color: '#7c3aed' }}>
            Artist Shops
          </span>
        </h1>
        <p className="mp-subtitle">Browse artist shops and explore their collections</p>
      </div>

      {/* Search bar */}
      {!loading && !error && shops.length > 0 && (
        <div className="mp-search-row">
          <div className="mp-search-box">
            <span className="mp-search-icon" aria-hidden="true">&#128269;</span>
            <input
              type="text"
              className="mp-search-input"
              placeholder="Search shops, artists..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button className="mp-search-clear" onClick={() => setSearch('')} aria-label="Clear">&times;</button>
            )}
          </div>
        </div>
      )}

      <div className="mp-stage" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 460 }}>
        {loading && (
          <div className="mp-state">
            <div className="mp-loading-ring" style={{ borderTopColor: '#7c3aed' }} />
            <p className="mp-state-text" style={{ color: '#7c3aed' }}>Loading artist shops…</p>
          </div>
        )}

        {!loading && error && (
          <div className="mp-state">
            <span className="mp-state-emoji">🏪</span>
            <p className="mp-state-text">{error}</p>
          </div>
        )}

        {!loading && !error && shops.length === 0 && (
          <div className="mp-state">
            <span className="mp-state-emoji">🎨</span>
            <p className="mp-state-text">No artist shops available yet.</p>
          </div>
        )}

        {!loading && !error && debouncedSearch && shops.length === 0 && (
          <div className="mp-state">
            <span className="mp-state-emoji">🔍</span>
            <p className="mp-state-text">No shops match "{search}"</p>
          </div>
        )}

        {!loading && !error && shops.length > 0 && (
          <div style={{ width: '100%', maxWidth: 1100, margin: '0 auto' }}>
            <Carousel
              items={shops}
              autoplay={shops.length > 3}
              autoplayDelay={5000}
              pauseOnHover
              onItemClick={handleShopClick}
            />
          </div>
        )}
      </div>


    </div>
  );
}
